import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { INDIAN_STATES, COUNTRIES } from "@/lib/india";

const selectCls =
  "h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

export function AddressFields({ value, onChange, showGstin = false, testidPrefix = "addr" }) {
  const v = value || {};
  const latest = useRef(v);
  latest.current = v;
  const set = (patch) => onChange({ ...latest.current, ...patch });

  const [pinStatus, setPinStatus] = useState("idle"); // idle | typing | loading | ok | error
  const [pinMsg, setPinMsg] = useState("");
  const [cities, setCities] = useState([]);
  const deb = useRef();

  const isIN = (v.country || "IN") === "IN";

  useEffect(() => {
    if (!v.country) {
      api.get("/geo/ip-country")
        .then((r) => set({ country: r.data.country || "IN" }))
        .catch(() => set({ country: "IN" }));
    }
    if ((v.country || "IN") === "IN" && /^\d{6}$/.test(v.pincode || "")) runLookup(v.pincode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLookup = (pin) => {
    setPinStatus("loading");
    api.get(`/geo/pincode/${pin}`)
      .then(({ data }) => {
        if (data.valid) {
          setPinStatus("ok");
          setPinMsg(`${data.district}, ${data.state}`);
          setCities(data.cities || []);
          set({ state: data.state, district: data.district, country: "IN", city: latest.current.city || (data.cities?.[0] || "") });
        } else {
          setPinStatus("error");
          setPinMsg(data.detail || "Invalid PIN code");
          setCities([]);
        }
      })
      .catch(() => { setPinStatus("error"); setPinMsg("Could not verify PIN"); setCities([]); });
  };

  const onPin = (pin) => {
    set({ pincode: pin });
    clearTimeout(deb.current);
    if (!isIN) { setPinStatus("idle"); return; }
    if (!/^\d{6}$/.test(pin)) { setPinStatus(pin ? "typing" : "idle"); setPinMsg(""); setCities([]); return; }
    deb.current = setTimeout(() => runLookup(pin), 500);
  };

  const onCountry = (code) => {
    setPinStatus("idle"); setPinMsg(""); setCities([]);
    // clear India-specific auto-filled values when leaving India for a clean slate
    if (code !== "IN") set({ country: code, state: "", district: "" });
    else set({ country: code });
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Full name</label>
        <Input value={v.name || ""} onChange={(e) => set({ name: e.target.value })} placeholder="Full name" data-testid={`${testidPrefix}-name`} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Phone</label>
        <Input value={v.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="10-digit mobile" data-testid={`${testidPrefix}-phone`} />
      </div>

      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs font-medium text-slate-500">Address line 1</label>
        <Input value={v.line1 || ""} onChange={(e) => set({ line1: e.target.value })} placeholder="House / flat, building, street" data-testid={`${testidPrefix}-line1`} />
      </div>
      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs font-medium text-slate-500">Address line 2 (optional)</label>
        <Input value={v.line2 || ""} onChange={(e) => set({ line2: e.target.value })} placeholder="Area, landmark" data-testid={`${testidPrefix}-line2`} />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Country</label>
        <select className={selectCls} value={v.country || "IN"} onChange={(e) => onCountry(e.target.value)} data-testid={`${testidPrefix}-country`}>
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">{isIN ? "PIN code" : "Postal / ZIP code"}</label>
        <div className="relative">
          <Input
            value={v.pincode || ""}
            onChange={(e) => onPin(e.target.value.replace(/[^0-9A-Za-z]/g, ""))}
            placeholder={isIN ? "6-digit PIN" : "Postal code"}
            inputMode={isIN ? "numeric" : "text"}
            maxLength={isIN ? 6 : 12}
            data-testid={`${testidPrefix}-pincode`}
          />
          {pinStatus === "loading" && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
          {pinStatus === "ok" && <CheckCircle2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
          {pinStatus === "error" && <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-rose-500" />}
        </div>
        {isIN && pinMsg && (
          <p className={`mt-1 text-xs ${pinStatus === "ok" ? "text-emerald-600" : "text-rose-500"}`} data-testid={`${testidPrefix}-pin-msg`}>{pinMsg}</p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">State</label>
        {isIN ? (
          <select className={selectCls} value={v.state || ""} onChange={(e) => set({ state: e.target.value })} data-testid={`${testidPrefix}-state`}>
            <option value="">Select state</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <Input value={v.state || ""} onChange={(e) => set({ state: e.target.value })} placeholder="State / Province" data-testid={`${testidPrefix}-state`} />
        )}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">District</label>
        <Input value={v.district || ""} onChange={(e) => set({ district: e.target.value })} placeholder="District" data-testid={`${testidPrefix}-district`} />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">City / Town</label>
        <Input value={v.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder="City / Town" list={cities.length ? `${testidPrefix}-cities` : undefined} data-testid={`${testidPrefix}-city`} />
        {cities.length > 0 && (
          <datalist id={`${testidPrefix}-cities`}>{cities.map((c) => <option key={c} value={c} />)}</datalist>
        )}
      </div>

      {showGstin && (
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-500">GSTIN (optional)</label>
          <Input value={v.gstin || ""} onChange={(e) => set({ gstin: e.target.value })} placeholder="For a GST invoice" data-testid={`${testidPrefix}-gstin`} />
        </div>
      )}
    </div>
  );
}
